<template>
  <div class="formulario-modelo">
    <h1>Gestión de Modelo</h1>

    <!-- Selección de Tipo de Modelo -->
    <div class="form-group">
      <label for="tipoModelo">Tipo de Modelo</label>
      <div class="select-con-boton">
        <select id="tipoModelo" v-model="tipoSeleccionado">
          <option disabled value="">Seleccione un tipo</option>
          <option v-for="t in tiposUnicos" :key="t.tipo" :value="t.tipo">
            {{ t.tipo }}
          </option>
          <option v-for="t in tiposTemporal" :key="'tmp-' + t" :value="t">
            {{ t }} (nuevo)
          </option>
        </select>
        <button type="button" class="btn guardar btn-agregar" @click="mostrarInputNuevoTipo = true" v-if="!mostrarInputNuevoTipo">+ Agregar tipo</button>
      </div>
      <div v-if="mostrarInputNuevoTipo" class="input-nuevo">
        <input type="text" v-model="nuevoTipoNombre" placeholder="Nombre del nuevo tipo" />
        <button type="button" class="btn guardar btn-agregar" @click="agregarTipoTemporal">Agregar</button>
        <button type="button" class="btn salir btn-agregar" @click="mostrarInputNuevoTipo = false; nuevoTipoNombre = ''">Cancelar</button>
      </div>
    </div>

    <div class="form-group">
      <label for="nombreModelo">Nombre del Modelo</label>
      <input id="nombreModelo" type="text" v-model="nombreModelo" />
    </div>

    <div class="form-group">
      <label for="fechaCreacion">Fecha de creación</label>
      <input
        id="fechaCreacion"
        type="date"
        v-model="fechaCreacion"
        :max="fechaMaxima"
      />
    </div>

    <!-- Vigencia normativa: rango de años, distinto de la fecha de creación -->
    <div class="form-group">
      <label for="vigenciaInicio">Vigencia del modelo</label>
      <div class="rango-vigencia">
        <input
          id="vigenciaInicio"
          type="number"
          min="1900"
          max="2200"
          step="1"
          placeholder="2020"
          v-model.number="vigenciaInicio"
        />
        <span class="rango-separador">—</span>
        <input
          id="vigenciaFin"
          type="number"
          min="1900"
          max="2200"
          step="1"
          placeholder="2025"
          v-model.number="vigenciaFin"
        />
      </div>
      <small class="ayuda">Años de inicio y fin de vigencia (ej. 2020-2025).</small>
    </div>

    <div class="form-group">
      <label for="acuerdo">Acuerdo</label>
      <input
        id="acuerdo"
        type="text"
        maxlength="150"
        placeholder="Ejemplo: Acuerdo 02 de 2005"
        v-model="acuerdo"
      />
      <small class="ayuda">Acto administrativo que adopta el modelo.</small>
    </div>

    <div class="form-group">
      <label>Escala de calificación</label>
      <div class="escala-toggle">
        <button
          type="button"
          :class="['escala-opcion', escalaMax === 5 ? 'activa' : '']"
          @click="escalaMax = 5"
        >0 a 5</button>
        <button
          type="button"
          :class="['escala-opcion', escalaMax === 10 ? 'activa' : '']"
          @click="escalaMax = 10"
        >1 a 10</button>
      </div>
    </div>

    <div class="form-group">
      <label for="cantidadNiveles">Niveles</label>
      <input
        id="cantidadNiveles"
        type="number"
        min="1"
        placeholder="Ejemplo: 3"
        v-model.number="cantidadNiveles"
        @input="generarNiveles"
      />
    </div>

    <div class="divider"></div>

    <!-- Listado de niveles con inputs -->
    <div v-if="niveles.length > 0" class="niveles-listado">
      <h3>Nombre los Niveles</h3>
      <div v-for="(nombre, index) in niveles" :key="index" class="form-group">
        <label :for="'nivel-' + index">Nivel {{ index + 1 }}</label>
        <input
          :id="'nivel-' + index"
          type="text"
          placeholder="Nombre del Nivel"
          v-model="niveles[index]"
        />
      </div>
    </div>

    <div class="botones">
      <router-link :to="{ name: 'Inicio' }">
        <button class="btn salir" :disabled="loading || submitted">Salir</button>
      </router-link>
      <button 
        class="btn guardar" 
        @click.prevent="sendModelo" 
        :disabled="loading || submitted"
      >
        {{ loading ? 'Creando modelo...' : submitted ? 'Modelo creado' : 'Guardar' }}
      </button>
    </div>

    <!-- Mensaje de feedback -->
    <p v-if="feedback" :class="['feedback', submitted ? 'success' : 'error']">
      {{ feedback }}
    </p>

    <!-- Mensaje flotante de confirmación -->
    <MensajeFlotante
      :visible="mensajeVisible"
      :mensaje="mensajeTexto"
      :tipo="mensajeTipo"
      @cerrar="mensajeVisible = false"
    />
  </div>
</template>

<script lang="ts" setup>
/**
 * Alta y listado de modelos de calidad.
 *
 * El modelo es la plantilla general: aquí no se define área ni ámbito, eso llega
 * al crear una ejecución.
 *
 * ── Enrutado ─────────────────────────────────────────────────────
 *   `Gestion` (CrearModelo) — permiso `modelos.crear`
 *
 * Vista enrutada. Obtiene sus datos a través de stores Pinia: no llama a `http`
 * ni a los servicios directamente.
 */

import { ref, Ref, onMounted, computed } from 'vue'
import useModelo from '@/store/GestionModelo'
import { useGestionTipoModelos } from '@/store/GestionTipoModelo'
import router from '@/router'
import type EstructuraModelo from '@/interfaces/EstructuraModelo'
import MensajeFlotante from '@/components/MensajeFlotante.vue'

const nombreModelo: Ref<string> = ref('')
const escalaMax = ref<5 | 10>(5)
const cantidadNiveles: Ref<number> = ref(0)
const niveles: Ref<string[]> = ref([])
const store = useModelo()
const tipoModeloStore = useGestionTipoModelos()
const tipoSeleccionado = ref<string>('')
const feedback = ref('')
const loading = ref(false)
const submitted = ref(false)
const mensajeVisible = ref(false)
const mensajeTexto = ref('')
const mensajeTipo = ref<'positivo' | 'error'>('positivo')
const tiposTemporal = ref<string[]>([])
const mostrarInputNuevoTipo = ref(false)
const nuevoTipoNombre = ref('')
const fechaCreacion = ref<string>(hoyFormatoISODate())
const fechaMaxima = computed(() => hoyFormatoISODate())
// Vigencia normativa (rango de años) y acuerdo que adopta el modelo.
const vigenciaInicio = ref<number | null>(null)
const vigenciaFin = ref<number | null>(null)
const acuerdo = ref<string>('')

function agregarTipoTemporal() {
  const nombre = nuevoTipoNombre.value.trim()
  if (!nombre) return
  if (tiposUnicos.value.some(t => t.tipo === nombre) || tiposTemporal.value.includes(nombre)) {
    mostrarMensaje('Este tipo de modelo ya existe.', 'error')
    return
  }
  tiposTemporal.value.push(nombre)
  tipoSeleccionado.value = nombre
  nuevoTipoNombre.value = ''
  mostrarInputNuevoTipo.value = false
}


function mostrarMensaje(texto: string, tipo: 'positivo' | 'error') {
  mensajeTexto.value = texto
  mensajeTipo.value = tipo
  mensajeVisible.value = true
}

const tiposUnicos = computed(() => {
  const seen = new Set<string>()
  const result: { tipo: string }[] = []

  for (const t of tipoModeloStore.tipos) {
    if (!seen.has(t.tipo)) {
      seen.add(t.tipo)
      result.push({ tipo: t.tipo })
    }
  }

  return result
})

onMounted(async () => {
  await tipoModeloStore.getTipoModelos()
})

// Ajusta el array niveles a la nueva cantidad
const generarNiveles = (): void => {
  const n = Math.floor(Number(cantidadNiveles.value || 0))
  if (!n || n < 1) {
    niveles.value = []
    return
  }
  const prev = niveles.value || []
  niveles.value = Array.from({ length: n }, (_, i) => prev[i] ?? '')
}

// Valida el rango de vigencia con las mismas reglas del backend.
// Devuelve el mensaje de error, o null si es válido (incluye "sin registrar").
function validarVigencia(): string | null {
  const inicio = vigenciaInicio.value
  const fin = vigenciaFin.value

  if (inicio == null && fin == null) return null
  if (inicio == null || fin == null) {
    return 'La vigencia requiere el año inicial y el año final.'
  }
  if (!Number.isInteger(inicio) || !Number.isInteger(fin)) {
    return 'Los años de vigencia deben ser números enteros.'
  }
  if (inicio < 1900 || inicio > 2200 || fin < 1900 || fin > 2200) {
    return 'Los años de vigencia deben estar entre 1900 y 2200.'
  }
  if (fin < inicio) {
    return 'El año final de la vigencia no puede ser anterior al año inicial.'
  }
  return null
}

// Obtener la fecha actual
function hoyFormatoISODate(): string {
  const d = new Date()
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

// Enviar modelo
const sendModelo = async () => {
  feedback.value = ''
  
  if (!tipoSeleccionado.value) {
    feedback.value = 'Debe seleccionar un tipo de modelo.'
    return
  }

  if (!nombreModelo.value?.trim()) {
    feedback.value = 'El nombre del modelo es obligatorio.'
    return
  }

  if (!cantidadNiveles.value || cantidadNiveles.value < 1) {
    feedback.value = 'La cantidad de niveles es obligatoria y debe ser ≥ 1.'
    return
  }

  if (!fechaCreacion.value) {
    feedback.value = 'La fecha de creación es obligatoria.'
    return
  }

  const errorVigencia = validarVigencia()
  if (errorVigencia) {
    feedback.value = errorVigencia
    return
  }

  const nombresNiveles = niveles.value.map(n => n.trim()).filter(n => n !== '')
  if (nombresNiveles.length !== cantidadNiveles.value) {
    feedback.value = 'Debes completar el nombre de todos los niveles.'
    return
  }

  // Si el tipo es temporal, crearlo primero
  let tipoEncontrado
  const esTipoTemporal = tiposTemporal.value.includes(tipoSeleccionado.value)

  if (esTipoTemporal) {
    const nuevoTipo = await tipoModeloStore.crearTipoModelo({ tipo: tipoSeleccionado.value })
    if (!nuevoTipo) {
      feedback.value = 'Error al crear el nuevo tipo de modelo.'
      return
    }
    tipoEncontrado = nuevoTipo
    tiposTemporal.value = tiposTemporal.value.filter(t => t !== tipoSeleccionado.value)
  } else {
    tipoEncontrado = tipoModeloStore.tipos.find(t => t.tipo === tipoSeleccionado.value)
  }

  if (!tipoEncontrado?.id_tipo_modelo) {
    feedback.value = 'Tipo de modelo inválido.'
    return
  }

  try {
    loading.value = true

    const modeloPayload: Omit<EstructuraModelo, 'id_modelo'> = {
      nombre: nombreModelo.value.trim(),
      niveles: nombresNiveles,
      estado: 'edicion',
      fecha_creacion: fechaCreacion.value,
      id_tipo_modelo: tipoEncontrado.id_tipo_modelo,
      escala_max: escalaMax.value,
      vigencia_inicio: vigenciaInicio.value ?? null,
      vigencia_fin: vigenciaFin.value ?? null,
      acuerdo: acuerdo.value.trim() || null,
    }

    const result = await store.crearModeloConRaiz(modeloPayload)
    if (!result.ok) {
      mostrarMensaje('Error al guardar Modelo', 'error')
      return
    }
    submitted.value = true
    mostrarMensaje('Modelo creado con éxito', 'positivo')
    setTimeout(() => { router.push({ name: 'ListaVersiones' }) }, 2500)
  } catch (err: any) {
    console.error(err)
    mostrarMensaje(err?.message || 'Error al crear el modelo', 'error')
  } finally {
    loading.value = false
  }
}
</script>
  
<style scoped>
.formulario-modelo {
  background-color: white;
  padding: var(--space-lg);
  border-radius: var(--radius-lg);
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  box-shadow: var(--shadow-md);
  box-sizing: border-box;
  margin: 20px;
  font-family: var(--font-family);
}

h1 {
  margin-bottom: var(--space-lg);
  font-size: var(--font-size-2xl);
  font-weight: 700;
  letter-spacing: 0.01em;
  line-height: var(--line-height-tight);
}

.form-group {
  margin-bottom: var(--space-base);
  display: flex;
  flex-direction: column;
}

.form-group label {
  margin-bottom: 6px;
  font-weight: 500;
  font-size: var(--font-size-sm);
  color: var(--text-primary);
}

.nivel-group {
  display: flex;
  gap: var(--space-base);
  align-items: flex-start;
}

input, select {
  padding: 0.75rem;
  min-height: 42px;
  border: 1px solid #e6e5e5;
  border-radius: var(--radius-md);
  font-size: var(--font-size-base);
  font-family: var(--font-family);
  background-color: #f9f9f9;
  transition: border-color var(--transition-fast), box-shadow var(--transition-fast);
  box-sizing: border-box;
}

select {
  height: auto;
  line-height: 1.4;
  padding-right: 2rem;
  appearance: auto;
}

.select-con-boton {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.select-con-boton select {
  flex: 1;
}

.input-nuevo {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  margin-top: 0.5rem;
}

.input-nuevo input {
  flex: 1;
}

.btn-agregar {
  padding: 6px 14px;
  font-size: var(--font-size-sm);
  height: auto;
  white-space: nowrap;
}

input:focus, select:focus {
  outline: none;
  border-color: #0e69a0;
  box-shadow: 0 0 0 3px rgba(14, 105, 160, 0.1);
}

.opcional {
  background-color: #f2f2f2;
  padding: var(--space-base);
  border-radius: var(--radius-md);
  border: 1px dashed #b6b6b6;
}

.divider {
  border: none;
  border-bottom: 1px solid #949393;
  margin: var(--space-lg) 0;
}

.deshabilitado {
  opacity: 0.5;
  pointer-events: none;
}

.botones {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-md);
  margin-top: var(--space-lg);
}

.btn {
  padding: 8px 20px;
  height: 40px;
  font-weight: 600;
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

.btn.salir {
  background-color: white;
  border: 1px solid #ccc;
}

.btn.salir:hover {
  background-color: rgb(212, 26, 26);
  color: white;
}

.btn.guardar {
  background-color: #eea222;
  color: black;
}

.btn.guardar:hover {
  background-color: #0e69a0;
  color: white;
}

.feedback {
  margin-top: var(--space-base);
  color: #b00020;
}

.resumen-niveles {
  margin-top: var(--space-base);
  padding: var(--space-base);
  border: 1px solid #ddd;
  border-radius: var(--radius-md);
  background-color: #fafafa;
}

.resumen-niveles h3 {
  margin-bottom: var(--space-base);
  font-size: var(--font-size-lg);
  font-weight: 600;
  color: #333;
}

.resumen-niveles ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.resumen-niveles li {
  padding: var(--space-xs) 0;
  font-size: var(--font-size-base);
}

.feedback {
  margin-top: var(--space-base);
  font-weight: 600;
  padding: var(--space-sm) var(--space-base);
  border-radius: var(--radius-sm);
  font-size: var(--font-size-sm);
}

.feedback.success {
  background-color: #d4edda;
  color: #155724;
}

.feedback.error {
  background-color: #f8d7da;
  color: #721c24;
}

input[type="date"] {
  cursor: pointer;
  max-width: 220px;
}

input[type="date"]::-webkit-calendar-picker-indicator {
  cursor: pointer;
  opacity: 0.6;
}

input[type="date"]::-webkit-calendar-picker-indicator:hover {
  opacity: 1;
}

.rango-vigencia {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

.rango-vigencia input {
  max-width: 120px;
}

.rango-separador {
  color: #777;
  font-weight: 600;
}

.ayuda {
  margin-top: 6px;
  font-size: var(--font-size-xs, 0.75rem);
  color: #777;
}

.escala-toggle {
  display: flex;
  gap: 0;
  border: 1px solid #e6e5e5;
  border-radius: var(--radius-md);
  overflow: hidden;
  width: fit-content;
}

.escala-opcion {
  padding: 0.6rem 1.5rem;
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  font-weight: 500;
  background-color: #f9f9f9;
  color: #555;
  border: none;
  cursor: pointer;
  transition: background-color 0.15s, color 0.15s;
}

.escala-opcion:not(:last-child) {
  border-right: 1px solid #e6e5e5;
}

.escala-opcion.activa {
  background-color: #0e69a0;
  color: white;
  font-weight: 600;
}

.escala-opcion:not(.activa):hover {
  background-color: #eef4fa;
  color: #0e69a0;
}

</style>